import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';

const CORRECT_PASSWORD = process.env.REACT_APP_ACCESS_PASSWORD || 'vouch2024';

// Marketing site: local in dev, subdomain in prod
const MARKETING_URL = process.env.REACT_APP_MARKETING_URL ||
  (window.location.hostname === 'localhost'
    ? 'http://localhost:3001'
    : 'https://marketing.voucherjobs.com');

export default function AuthPage() {
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [authenticated, setAuthenticated] = useState(false);
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const returnTo = searchParams.get('returnTo');

  useEffect(() => {
    if (sessionStorage.getItem('vouch_auth') === 'authenticated') {
      setAuthenticated(true);
      // If already authenticated and there's a returnTo, redirect immediately
      if (returnTo) {
        navigate(returnTo, { replace: true });
      }
    }
  }, [returnTo, navigate]);

  const authenticate = () => {
    if (authenticated) return true;
    if (password === CORRECT_PASSWORD) {
      sessionStorage.setItem('vouch_auth', 'authenticated');
      // Set cookie on parent domain so marketing subdomain can read it
      const domain = window.location.hostname === 'localhost' ? '' : '; domain=.voucherjobs.com';
      document.cookie = `vouch_auth=authenticated; path=/${domain}; max-age=86400; SameSite=Lax; Secure`;
      setAuthenticated(true);
      setError('');
      return true;
    }
    setError('Incorrect password. Please try again.');
    setPassword('');
    return false;
  };

  const handleNavigate = (e) => {
    if (!authenticate()) e.preventDefault();
  };

  return (
    <div style={{
      minHeight: '100vh',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      background: 'linear-gradient(135deg, #0B1F3B 0%, #122B4F 50%, #18B6B2 100%)',
      fontFamily: "'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif",
      padding: '1rem',
    }}>
      <div style={{
        background: 'white',
        padding: '2.5rem',
        borderRadius: '1rem',
        boxShadow: '0 20px 60px rgba(11, 31, 59, 0.4)',
        width: '100%',
        maxWidth: '400px',
      }}>
        {/* Logo */}
        <div style={{ textAlign: 'center', marginBottom: '2rem' }}>
          <img
            src="/brand/vouch/assets/vector/vouch-wordmark-horizontal.svg"
            alt="VOUCH"
            height="48"
            style={{ display: 'inline-block' }}
          />
          <p style={{ color: '#6B7280', fontSize: '0.875rem', marginTop: '0.5rem' }}>
            Trusted network hiring
          </p>
        </div>

        {/* Password field */}
        <div style={{ marginBottom: '1.25rem' }}>
          <input
            type="password"
            value={password}
            onChange={(e) => { setPassword(e.target.value); setError(''); }}
            placeholder={authenticated ? 'Authenticated' : 'Enter access password'}
            disabled={authenticated}
            autoFocus={!authenticated}
            style={{
              width: '100%',
              padding: '0.75rem',
              border: `2px solid ${authenticated ? '#18B6B2' : '#e2e8f0'}`,
              borderRadius: '0.5rem',
              fontSize: '1rem',
              outline: 'none',
              transition: 'border-color 0.2s',
              boxSizing: 'border-box',
              background: authenticated ? '#f0fdfa' : 'white',
              color: authenticated ? '#0d9488' : '#0B1F3B',
            }}
            onFocus={(e) => { if (!authenticated) e.target.style.borderColor = '#18B6B2'; }}
            onBlur={(e) => { if (!authenticated) e.target.style.borderColor = '#e2e8f0'; }}
            onKeyDown={(e) => { if (e.key === 'Enter') authenticate(); }}
          />
        </div>

        {/* Error */}
        {error && (
          <div style={{
            padding: '0.75rem',
            background: '#fee2e2',
            border: '1px solid #fecaca',
            borderRadius: '0.5rem',
            color: '#dc2626',
            fontSize: '0.875rem',
            marginBottom: '1rem',
          }}>
            {error}
          </div>
        )}

        {/* Buttons */}
        <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
          {/* Value Proposition → marketing site */}
          <a
            href={MARKETING_URL}
            onClick={handleNavigate}
            style={{
              display: 'block',
              width: '100%',
              padding: '1rem',
              background: '#0B1F3B',
              color: 'white',
              border: 'none',
              borderRadius: '0.5rem',
              fontSize: '1rem',
              fontWeight: '700',
              textAlign: 'center',
              textDecoration: 'none',
              boxShadow: '0 4px 12px rgba(11, 31, 59, 0.4)',
              boxSizing: 'border-box',
              letterSpacing: '0.02em',
              cursor: 'pointer',
            }}
          >
            See Value Proposition
          </a>

          {/* Try the Live Demo → 3-pane view */}
          <a
            href="/demo"
            onClick={handleNavigate}
            style={{
              display: 'block',
              width: '100%',
              padding: '1rem',
              background: 'linear-gradient(135deg, #18B6B2 0%, #0B1F3B 100%)',
              color: 'white',
              border: 'none',
              borderRadius: '0.5rem',
              fontSize: '1rem',
              fontWeight: '700',
              textAlign: 'center',
              textDecoration: 'none',
              boxShadow: '0 4px 12px rgba(24, 182, 178, 0.3)',
              boxSizing: 'border-box',
              letterSpacing: '0.02em',
              cursor: 'pointer',
            }}
          >
            Try the Live Demo
          </a>

          {/* Individual role buttons — PWA color coding */}
          <div style={{
            display: 'flex',
            gap: '0.5rem',
            marginTop: '-0.25rem',
          }}>
            <a
              href="/candidate"
              onClick={handleNavigate}
              style={{
                flex: 1,
                padding: '0.625rem 0.5rem',
                background: '#18B6B2',
                color: 'white',
                border: 'none',
                borderRadius: '0.5rem',
                fontSize: '0.8125rem',
                fontWeight: '700',
                textAlign: 'center',
                textDecoration: 'none',
                boxShadow: '0 2px 8px rgba(24, 182, 178, 0.3)',
                cursor: 'pointer',
              }}
            >
              Candidate
            </a>
            <a
              href="/voucher"
              onClick={handleNavigate}
              style={{
                flex: 1,
                padding: '0.625rem 0.5rem',
                background: '#059669',
                color: 'white',
                border: 'none',
                borderRadius: '0.5rem',
                fontSize: '0.8125rem',
                fontWeight: '700',
                textAlign: 'center',
                textDecoration: 'none',
                boxShadow: '0 2px 8px rgba(5, 150, 105, 0.3)',
                cursor: 'pointer',
              }}
            >
              Voucher
            </a>
            <a
              href="/hiring-manager"
              onClick={handleNavigate}
              style={{
                flex: 1,
                padding: '0.625rem 0.5rem',
                background: '#0B1F3B',
                color: 'white',
                border: 'none',
                borderRadius: '0.5rem',
                fontSize: '0.8125rem',
                fontWeight: '700',
                textAlign: 'center',
                textDecoration: 'none',
                boxShadow: '0 2px 8px rgba(11, 31, 59, 0.3)',
                cursor: 'pointer',
              }}
            >
              Hiring Mgr
            </a>
          </div>
        </div>

        {/* Footer */}
        <div style={{ textAlign: 'center', marginTop: '1.5rem' }}>
          <p style={{ fontSize: '0.75rem', color: '#94a3b8' }}>
            This demo is password protected
          </p>
        </div>
      </div>
    </div>
  );
}
